import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import { hasStockOperationPermission } from "../../utils/purchasingStockPermissions";

// Pending dialogs and queued writes remain tied to the account and hotel that opened them.
export default function useStockOperationAccess(businessUnitId) {
  const [user, setUser] = useState(readStoredUser);
  const mounted = useRef(true);
  const expectedUserId = useRef(user?.id);
  const selectedUnit = useRef(businessUnitId);
  selectedUnit.current = businessUnitId;
  useEffect(() => {
    mounted.current = true;
    const update = () => {
      const candidate = readStoredUser();
      setUser((current) => JSON.stringify(current) === JSON.stringify(candidate) ? current : candidate);
    };
    update();
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      mounted.current = false;
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, [businessUnitId]);
  const can = useCallback((workspace, action = "view", current = false) => {
    const candidate = current ? readStoredUser() : user;
    return mounted.current && Boolean(candidate?.id) && String(candidate.id) === String(expectedUserId.current) &&
      Number.isSafeInteger(Number(businessUnitId)) && Number(businessUnitId) > 0 &&
      Number(candidate.active_business_unit_id) === Number(businessUnitId) &&
      Number(selectedUnit.current) === Number(businessUnitId) &&
      hasStockOperationPermission(candidate, workspace, action);
  }, [user, businessUnitId]);
  const requestConfig = useCallback((workspace, action = "view") => {
    if (!can(workspace, action, true)) throw new Error("The document changed or your access changed. Close and reopen it before continuing.");
    return { headers: { "X-Pilcore-Business-Unit-Id": businessUnitId }, pilcoreExpectedUserId: expectedUserId.current, params: { business_unit_id: businessUnitId } };
  }, [businessUnitId, can]);
  const contextKey = JSON.stringify([user?.id, user?.active_business_unit_id, user?.permissions, businessUnitId]);
  return useMemo(() => ({ can, requestConfig, contextKey }), [can, requestConfig, contextKey]);
}
